import { DeclarationWorkbookViewer } from "@/components/common/ExcelViewer/DeclarationWorkbookViewer"
import { DeclarationDocumentViewer } from "@/components/common/PdfViewer/DeclarationDocumentViewer"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useState } from "react"
import { Link } from "react-router"
import { useDeclarationReadModel } from "../../api/declarationsApi"
import type { FilterToken } from "./review/studio/TableSearch"
import { useCmdFToggle, useDocumentBlobUrl } from "./review/studio/flowShared"
import { declarationReviewPath } from "./routes"

interface DeclarationInlineDocumentPreviewProps {
  declId: string
}

/** A plain "open the full studio" escape hatch — used both while the kind is
 *  still loading and when the read model names neither modality (a legacy
 *  run predating `document_kind`, or a declaration with no document at all).
 *  Never a guess at which viewer to mount. */
function OpenInStudioFallback({
  declId,
  message,
}: {
  declId: string
  message?: string
}) {
  const { translate } = useTranslation()
  return (
    <div className="flex h-[60vh] flex-col items-center justify-center gap-2 px-6 text-center text-[12px] text-grey5">
      {message && <span>{message}</span>}
      <Link
        to={declarationReviewPath(declId)}
        className="text-yellow1 hover:underline"
      >
        {translate("MC_DECL_RECORD_OPEN_IN_STUDIO")}
      </Link>
    </div>
  )
}

/**
 * The declaration panel's own document preview — mounted ONLY while its
 * "Show document" section is expanded (the caller renders this component
 * conditionally), so a reviewer who never opens it never pays for the byte
 * fetch. No highlights, no evidence sources: this is a plain read of the
 * file, not the review studio's citation surface.
 *
 * Which viewer reads the bytes is `readModel.document_kind` — the same
 * signal `DocumentPane`/`StudioV2Layout` key off (never inferred from the
 * row's own `docType`, a dead wire field no endpoint populates — see
 * DeclarationListV2's own finding). Until that read model resolves, or when
 * it names neither `"workbook"` nor `"pdf"`, this renders the studio link
 * rather than guessing a viewer — a legacy declaration with no
 * `document_kind` is exactly the case a guess would render wrong.
 */
export function DeclarationInlineDocumentPreview({
  declId,
}: DeclarationInlineDocumentPreviewProps) {
  const { translate } = useTranslation()
  // Opted back in to the refocus refetch the review page turns off: this panel
  // has no arrival hydration, so a refocus is what refreshes a read model it
  // fetched mid-extraction (no `document_kind` yet, so no viewer).
  const { data: readModel, isPending: isKindPending } = useDeclarationReadModel(
    declId,
    { refetchOnWindowFocus: true },
  )
  const documentKind = readModel?.document_kind
  const isWorkbook = documentKind === "workbook"
  // Gate the byte fetch on the kind actually resolving — before this fix it
  // fired unconditionally alongside the read-model request, fetching bytes
  // for a document it didn't yet know how to render (or couldn't render at
  // all, once `documentKind` turns out to be neither modality below).
  const kindResolved = documentKind === "workbook" || documentKind === "pdf"
  const {
    url: docBlobUrl,
    isPending: isDocPending,
    isError: isDocError,
  } = useDocumentBlobUrl(declId, isWorkbook ? "workbook" : "pdf", kindResolved)

  const [barOpen, setBarOpen] = useState(false)
  const [tokens, setTokens] = useState<FilterToken[]>([])
  const [search, setSearch] = useState("")
  // ⌘/Ctrl+F toggles the document search bar — must run unconditionally,
  // before any early return below, so hook order stays stable regardless of
  // which branch this render takes (see StudioScreenA's identical call).
  useCmdFToggle(setBarOpen)

  if (isKindPending) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <span
          className="h-4 w-4 animate-spin rounded-full border-2 border-grey5 border-t-transparent"
          aria-hidden="true"
        />
      </div>
    )
  }

  if (documentKind !== "workbook" && documentKind !== "pdf") {
    return (
      <OpenInStudioFallback
        declId={declId}
        message={translate("MC_DECL_RECORD_DOC_KIND_UNKNOWN")}
      />
    )
  }

  if (isDocPending) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <span
          className="h-4 w-4 animate-spin rounded-full border-2 border-grey5 border-t-transparent"
          aria-hidden="true"
        />
      </div>
    )
  }

  if (!docBlobUrl || isDocError) {
    return (
      <OpenInStudioFallback
        declId={declId}
        message={translate("MC_DECL_V2_DOC_UNAVAILABLE")}
      />
    )
  }

  // Both viewers assume `height:100%` from their nearest sized ancestor
  // (`.doc-viewer` in studioScreenA.css) so their own internal scroll
  // container — not this wrapper — is what scrolls, and the find/evidence
  // dock (`position:absolute; bottom:0` against that same ancestor) stays
  // pinned to this box's bottom rather than floating off with page scroll.
  if (isWorkbook) {
    return (
      <div className="h-[60vh] overflow-hidden rounded-md border border-grey4">
        <DeclarationWorkbookViewer
          fileUrl={docBlobUrl}
          open={barOpen}
          onOpenChange={setBarOpen}
          tokens={tokens}
          search={search}
          onTokensChange={setTokens}
          onSearchChange={setSearch}
          placeholder={translate("MC_DECL_V2_DOC_FIND_PLACEHOLDER")}
        />
      </div>
    )
  }

  return (
    <div className="h-[60vh] overflow-hidden rounded-md border border-grey4">
      <DeclarationDocumentViewer
        fileUrl={docBlobUrl}
        highlight={null}
        sources={[]}
        activeStatementId={null}
        open={barOpen}
        onOpenChange={setBarOpen}
        tokens={tokens}
        search={search}
        onTokensChange={setTokens}
        onSearchChange={setSearch}
        placeholder={translate("MC_DECL_V2_DOC_FIND_PLACEHOLDER")}
      />
    </div>
  )
}
